import React from 'react';
import AiStatusDot from '../../AiAdmin/Components/AiStatusDot';
import { traceOutcomeDescriptor } from '../../../Services/AiDryRunService';

/**
 * WHAT THE AGENT ACTUALLY LOOKED UP BEFORE IT WROTE THAT.
 *
 * ── THIS IS THE TRUST PANEL ──
 * A reviewer decides whether to believe a draft in a few seconds, and what
 * decides it is not the prose — fluent prose is what a model produces when it
 * is inventing. It is whether the agent READ the booking before telling the
 * customer about it. A backtest over 61 real threads found 16% of
 * model-written drafts asserting booking facts that appeared nowhere: one laid
 * out a complete two-day itinerary, a named hotel and six leg times, none of
 * it on the record. A trace showing that the agent called nothing at all is
 * the fastest way to recognise one of those.
 *
 * So: which tools, in order, with what happened to each and how long it took,
 * plus the guidance version the run was steered by. Compact on purpose — the
 * arguments and the returned payloads belong on the AI Admin dry run, where
 * somebody is debugging a rule rather than judging a reply.
 *
 * ── THE VOCABULARY IS THE DRY RUN'S ──
 * `traceOutcomeDescriptor` and its five words, including `held` for a write
 * that was captured rather than executed, which is the normal outcome for
 * every write in shadow mode. A second list of outcomes for the same steps
 * would drift from the one the admin screen reads.
 *
 * ── NO TRACE IS A REAL ANSWER, NOT AN EMPTY PANEL ──
 * An agent that looked nothing up still produced the draft above, and that
 * is the single most useful thing this panel can say about it.
 */

/**
 * WHICH PUBLISHED GUIDANCE THE RUN WAS STEERED BY — three answers, not two.
 *
 * `null` is a recorded fact: the run read the instruction store and nothing
 * was published, so it ran on the code-owned prompt alone. That is a
 * different answer from the field being absent, which means this run does not
 * report one at all. The same rule, and for the same reason, as
 * `AiAdmin/Components/AiDecisionModal`'s `instructionRev`.
 */
const guidanceLabel = (run) => {
  if (!run || !Object.prototype.hasOwnProperty.call(run, 'instruction_rev')) {
    return 'Not reported';
  }
  return run.instruction_rev === null ? 'Nothing published' : `v${run.instruction_rev}`;
};

/** `—` rather than a blank, so an absent fact reads as absent. */
const or = (value) => (value === 0 || value ? value : '—');

function AgentRunPanel({ run }) {
  if (!run) return null;

  const trace = run.trace || [];

  return (
    <section className="cs-run" data-testid="cs-run">
      <div className="cs-context__title">
        <i className="fa fa-magnifying-glass me-2" aria-hidden="true" />
        What the agent looked up
      </div>

      <dl className="cs-run__facts">
        <div>
          <dt>Guidance</dt>
          <dd>{guidanceLabel(run)}</dd>
        </div>
        <div>
          <dt>Took</dt>
          <dd>{run.duration_ms === undefined ? '—' : `${run.duration_ms} ms`}</dd>
        </div>
        <div>
          <dt>Model</dt>
          <dd className="cs-run__model">{run.model || '—'}</dd>
        </div>
        <div>
          <dt>Tokens</dt>
          <dd>
            {run.tokens
              ? `${or(run.tokens.input)} in · ${or(run.tokens.output)} out`
              : '—'}
          </dd>
        </div>
      </dl>

      {trace.length === 0 ? (
        <p className="cs-run__none mb-0">
          It called no tools at all. Everything in the draft above came from
          the message itself and from whatever the guidance says — nothing was
          read off a booking.
        </p>
      ) : (
        <ol className="cs-run__steps">
          {trace.map((step, index) => {
            const outcome = traceOutcomeDescriptor(step.outcome);
            return (
              <li
                className="cs-run__step"
                /* The position IS the identity: a trace is an ordered record
                   in which the same tool legitimately appears twice with
                   different arguments, and it is replaced wholesale by the
                   next run. A key built from the tool name would collide on
                   the second call and collapse two steps into one, which is a
                   trace telling a lie about what the agent did. */
                // eslint-disable-next-line react/no-array-index-key
                key={`${index}-${step.tool}`}
              >
                <code className="cs-run__tool">{step.tool || 'unnamed call'}</code>
                <span className="cs-run__outcome">
                  <AiStatusDot tone={outcome.tone} />
                  {outcome.label}
                </span>
                {step.duration_ms !== undefined && (
                  <span className="cs-run__took">{`${step.duration_ms} ms`}</span>
                )}
                {step.error && <span className="cs-run__error">{step.error}</span>}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

export default AgentRunPanel;
